import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { UNITS } from "@/data/units";

const UnitCard = ({ unit, t }) => {
  const data = t.units[unit.key];
  return (
    <article
      data-testid={`unit-card-${unit.key}`}
      className="unit-card relative rounded-md overflow-hidden border border-white/8 flex flex-col"
      style={{
        backgroundColor: "#0c1330",
        ["--accent-color"]: unit.accent,
      }}
    >
      {/* Image */}
      <div className="relative h-56 sm:h-64 lg:h-72 overflow-hidden">
        <img
          src={unit.image}
          alt={`EMKA ${data.title}`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,16,32,0.05) 0%, rgba(11,16,32,0.85) 100%)",
          }}
        />
        {/* accent line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[3px]"
          style={{ backgroundColor: unit.accent }}
        />
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-5 sm:p-6">
        <div className="mb-4">
          <h3
            className="font-display font-extrabold leading-none text-white"
            style={{ fontSize: "1.5rem", letterSpacing: "0.02em" }}
          >
            EMKA
          </h3>
          <div
            className="font-display font-extrabold leading-tight"
            style={{
              color: unit.accent,
              fontSize: "1.5rem",
              letterSpacing: "0.06em",
            }}
          >
            {data.title}
          </div>
        </div>

        <ul className="space-y-1.5 text-[13px] text-white/85 flex-1">
          {data.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span
                className="mt-[7px] inline-block h-1 w-1 rounded-full shrink-0"
                style={{ backgroundColor: unit.accent }}
              />
              <span className="leading-snug">{item}</span>
            </li>
          ))}
        </ul>

        <Link
          to={`/${unit.slug}`}
          data-testid={`view-more-${unit.key}`}
          className="view-more mt-6 inline-flex items-center justify-between gap-3 px-4 py-2.5 border text-[11.5px] font-semibold tracking-[0.18em] uppercase"
          style={{
            borderColor: "var(--emka-gold)",
            color: "var(--emka-gold)",
          }}
        >
          <span>{t.units.viewMore}</span>
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
};

const BusinessUnits = () => {
  const { t } = useLang();
  return (
    <section
      data-testid="business-units"
      className="relative bg-emka-2 py-16 sm:py-20 lg:py-24"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-12 lg:mb-16">
          <p
            className="text-[12px] sm:text-sm font-bold tracking-[0.32em] uppercase"
            style={{ color: "var(--emka-gold)" }}
          >
            {t.units.eyebrow}
          </p>
          <h2
            className="mt-3 font-display font-extrabold text-white uppercase tracking-wide"
            style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.2rem)" }}
          >
            {t.units.title}
          </h2>
          <span className="gold-divider mt-5" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-4">
          {UNITS.map((u) => (
            <UnitCard key={u.key} unit={u} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BusinessUnits;
